import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { api } from '@/api/client';
import { formatNumber, formatDateTime } from '@/utils/helpers';
import { TrendingUp, Package, AlertTriangle, DollarSign, ArrowUpRight, Zap, Trophy, Users, Wrench, ClipboardList, FileSignature } from 'lucide-react';
import { InvoiceForm } from '@/components/forms/InvoiceForm';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';

const KPICard = ({ label, value, icon: Icon, accent = false }) => (
  <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 transition-colors hover:border-[var(--border-strong)]">
    <div className="flex items-start justify-between mb-3">
      <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--text-muted)]">{label}</span>
      <Icon size={14} className={accent ? 'text-[var(--brand-cyan)]' : 'text-[var(--text-muted)]'} strokeWidth={1.75} />
    </div>
    <p className="text-3xl font-light tracking-tight text-[var(--text-primary)]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {value}
    </p>
  </div>
);

export const Dashboard = ({ onNavigate }) => {
  const currency = useCurrency();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [widgets, setWidgets] = useState(null);
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuickSale, setShowQuickSale] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [statsRes, activityRes, widgetsRes, plRes] = await Promise.all([
        api.getStats(),
        api.getActivity().catch(() => ({ data: [] })),
        api.getDashboardWidgets().catch(() => ({ data: null })),
        api.getProfitLoss({
          start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
          end_date: new Date().toISOString().split('T')[0]
        }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data.slice(0, 8));
      setWidgets(widgetsRes.data);
      setPl(plRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-[var(--text-muted)]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-medium tracking-tight text-[var(--text-primary)]">Dashboard</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">A snapshot of your business right now.</p>
        </div>
        <Button onClick={() => setShowQuickSale(true)} variant="accent" size="md" data-testid="quick-sale-button">
          <Zap size={14} strokeWidth={2} />
          Quick Sale
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Stock Value" value={formatCurrency(stats?.stock_value || 0, currency)} icon={Package} />
        <KPICard label="Total Revenue" value={formatCurrency(stats?.revenue || 0, currency)} icon={DollarSign} accent />
        <KPICard label="Active RMAs" value={stats?.active_rmas || 0} icon={AlertTriangle} />
        <KPICard label="Overdue Credits" value={formatCurrency(stats?.overdue_credits || 0, currency)} icon={TrendingUp} />
      </div>

      {/* P&L Snapshot (this month) */}
      {pl && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pl-snapshot">
          <Card title="Revenue · This Month">
            <p className="text-2xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400" data-testid="dash-pl-revenue">{formatCurrency(pl.revenue || 0, currency)}</p>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{pl.invoice_count || 0} invoices</p>
          </Card>
          <Card title="Approved Expenses · This Month">
            <p className="text-2xl font-semibold mt-1 text-red-600 dark:text-red-400" data-testid="dash-pl-expenses">{formatCurrency(pl.total_expenses || 0, currency)}</p>
          </Card>
          <Card title="Net Profit · This Month">
            <p className={`text-2xl font-semibold mt-1 ${(pl.net_profit || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-testid="dash-pl-net">
              {formatCurrency(pl.net_profit || 0, currency)}
            </p>
          </Card>
        </div>
      )}

      {/* Staff widgets row */}
      {widgets && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Top Salesperson · This Month">
            {widgets.top_salesperson_month ? (
              <div className="flex items-center gap-3" data-testid="top-salesperson">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                  <Trophy size={18} className="text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-base font-semibold text-[var(--text-primary)]">{widgets.top_salesperson_month.user_name}</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {widgets.top_salesperson_month.count} invoices · {formatCurrency(widgets.top_salesperson_month.total, currency)}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)] py-2">No sales this month yet.</p>
            )}
          </Card>
          <Card title="Pending RMAs">
            <div className="flex items-center gap-3" data-testid="pending-rmas-widget">
              <div className="w-10 h-10 rounded-md bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                <Wrench size={18} className="text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-3xl font-light tracking-tight">{widgets.pending_rmas || 0}</p>
            </div>
          </Card>
          <Card title="Pending Quotations">
            <div className="flex items-center gap-3" data-testid="pending-quotations-widget">
              <div className="w-10 h-10 rounded-md bg-cyan-100 dark:bg-cyan-900/40 flex items-center justify-center">
                <FileSignature size={18} className="text-cyan-600 dark:text-cyan-400" />
              </div>
              <p className="text-3xl font-light tracking-tight">{widgets.pending_quotations || 0}</p>
            </div>
          </Card>
        </div>
      )}

      {/* Sales-by-staff widgets */}
      {widgets && (widgets.today_by_staff?.length > 0 || widgets.month_by_staff?.length > 0 || widgets.active_rmas_by_technician?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card title="Today's Sales by Staff">
            {widgets.today_by_staff?.length ? (
              <div className="space-y-1.5" data-testid="today-by-staff">
                {widgets.today_by_staff.map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--surface-hover)]">
                    <span className="text-sm">{row.user_name}</span>
                    <div className="text-right">
                      <span className="text-sm font-semibold">{formatCurrency(row.total, currency)}</span>
                      <span className="text-xs text-[var(--text-muted)] ml-2">({row.count})</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)] py-2">No sales today.</p>
            )}
          </Card>
          <Card title="Monthly Sales by Staff">
            {widgets.month_by_staff?.length ? (
              <div className="space-y-1.5" data-testid="month-by-staff">
                {widgets.month_by_staff.slice(0, 6).map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--surface-hover)]">
                    <span className="text-sm">{row.user_name}</span>
                    <div className="text-right">
                      <span className="text-sm font-semibold">{formatCurrency(row.total, currency)}</span>
                      <span className="text-xs text-[var(--text-muted)] ml-2">({row.count})</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)] py-2">No sales this month.</p>
            )}
          </Card>
          <Card title="Active RMAs by Technician">
            {widgets.active_rmas_by_technician?.length ? (
              <div className="space-y-1.5" data-testid="rmas-by-tech">
                {widgets.active_rmas_by_technician.map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--surface-hover)]">
                    <span className="text-sm">{row.technician_name}</span>
                    <span className="text-sm font-semibold">{row.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--text-muted)] py-2">No active RMAs.</p>
            )}
          </Card>
        </div>
      )}

      {/* Alerts */}
      {(stats?.out_of_stock?.length > 0 || stats?.low_stock?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {stats?.out_of_stock?.length > 0 && (
            <Card title="Out of Stock">
              <div className="space-y-1.5">
                {stats.out_of_stock.slice(0, 5).map((product, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-[var(--surface-hover)] transition-colors">
                    <span className="text-sm text-[var(--text-primary)]">{product.name}</span>
                    <Badge variant="danger">Empty</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {stats?.low_stock?.length > 0 && (
            <Card title="Low Stock">
              <div className="space-y-1.5">
                {stats.low_stock.slice(0, 5).map((product, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-[var(--surface-hover)] transition-colors">
                    <span className="text-sm text-[var(--text-primary)]">{product.name}</span>
                    <Badge variant="warning">Low</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Recent Activity */}
      <Card title="Recent Activity">
        <div className="space-y-0.5">
          {activity.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] text-center py-6">No recent activity</p>
          ) : (
            activity.map((item) => (
              <div key={item.id} className="flex items-start gap-3 py-2.5 px-3 rounded-md hover:bg-[var(--surface-hover)] transition-colors">
                <div className="w-1.5 h-1.5 rounded-full bg-[var(--brand-cyan)] mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--text-primary)]">
                    <span className="font-medium">{item.user_name}</span>
                    <span className="text-[var(--text-muted)]"> {item.action} in </span>
                    <span className="text-[var(--text-secondary)]">{item.table}</span>
                  </p>
                  {item.details && (
                    <p className="text-xs text-[var(--text-muted)] truncate mt-0.5">{item.details}</p>
                  )}
                </div>
                <span className="text-[11px] text-[var(--text-muted)] flex-shrink-0">
                  {formatDateTime(item.timestamp)}
                </span>
              </div>
            ))
          )}
        </div>
      </Card>

      <InvoiceForm
        isOpen={showQuickSale}
        onClose={() => setShowQuickSale(false)}
        onSaved={() => {
          loadData();
          if (onNavigate) onNavigate('invoices');
        }}
      />
    </div>
  );
};
